import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { translateKey } from 'src/locales/translate';
import VisitModalEmptyState from 'src/sections/@dashboard/organization/components/OrganizationVisitModal/VisitModalEmptyState';

export const DOSAGE_LABELS: Record<string, string> = {
  Day: 'Dziennie',
  Week: 'Tygodniowo',
  Month: translateKey('hardcoded.miesiecznie'),
};

export interface MedicationSummaryRow {
  id?: string;
  name: string | undefined;
  /** The dosing as a vet reads it: "½ tabletki, 2 × dziennie". */
  dosing: string;
  /** How long the course runs: "7 dni", "do odwołania". */
  duration?: string;
  notes?: string | null;
  /** "Z magazynu: 2 jedn., podany w placówce", when taken from stock. */
  stock?: string | null;
}

interface MedicationSummaryTableProps {
  rows: MedicationSummaryRow[];
  onRowClick?: (index: number, row: MedicationSummaryRow) => void;
  canEdit?: boolean;
  emptyMessage?: string;
}

export default function MedicationSummaryTable({
  rows,
  onRowClick,
  canEdit = false,
  emptyMessage,
}: MedicationSummaryTableProps): React.ReactElement | null {
  if (rows.length === 0) {
    if (emptyMessage) {
      return <VisitModalEmptyState message={emptyMessage} />;
    }
    return null;
  }

  const isClickable = canEdit && Boolean(onRowClick);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nazwa</TableCell>
            <TableCell>{translateKey('medication.table.dosing')}</TableCell>
            <TableCell>{translateKey('medication.table.duration')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={row.id ?? index}
              hover={canEdit}
              onClick={isClickable ? () => onRowClick!(index, row) : undefined}
              sx={isClickable ? { cursor: 'pointer' } : undefined}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {row.name}
                </Typography>
                {row.stock && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="medication-row-stock"
                  >
                    {row.stock}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.dosing}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.duration || '-'}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
